import {
    createContext,
    useCallback,
    useContext,
    useSyncExternalStore,
} from 'react';

/**
 * Shared open/closed state for the directory browse left filter sidebar, so the
 * "Show Filters" / "Hide Filters" toggle in the pill bar and the collapsible
 * layout wrapping the grid stay in sync. Provided by DirectoryBrowsePage.
 */
export interface DirectoryFilterDrawer {
    open: boolean;
    toggle: () => void;
    /** True when this page actually has a sidebar to toggle. */
    hasSidebar: boolean;
}

// The sidebar's open state is persisted in sessionStorage, keyed by entity
// type, so it survives both SPA navigation (into a detail page and back) and a
// full page refresh. Keeping the panel open on Back also keeps the grid at its
// narrower column count, which is what makes the grid's scroll-position restore
// land correctly.
const STORAGE_KEY_PREFIX = 'directory-filter-open:';

const storageKey = (key: string): string => `${STORAGE_KEY_PREFIX}${key}`;

const listeners = new Map<string, Set<() => void>>();

const readOpen = (key: string): boolean => {
    if (typeof window === 'undefined') return false;
    try {
        return window.sessionStorage.getItem(storageKey(key)) === '1';
    } catch {
        // sessionStorage can throw (private mode, blocked cookies) — treat as closed.
        return false;
    }
};

const writeOpen = (key: string, open: boolean): void => {
    if (typeof window !== 'undefined') {
        try {
            window.sessionStorage.setItem(storageKey(key), open ? '1' : '0');
        } catch {
            // Ignore write failures (private mode / quota); state stays in-session.
        }
    }
    listeners.get(key)?.forEach((notify) => notify());
};

const subscribe = (key: string, notify: () => void): (() => void) => {
    const set = listeners.get(key) ?? new Set<() => void>();
    set.add(notify);
    listeners.set(key, set);
    return () => set.delete(notify);
};

/**
 * Read + toggle the persisted sidebar open state for an entity.
 *
 * Backed by useSyncExternalStore so the value is read *synchronously* on a
 * client remount — hitting Back from a detail page restores the panel (and the
 * grid columns) on the very first render, so the grid's scroll restore lands at
 * the right layout. During SSR and the one-time hydration it reports `false`
 * (the server snapshot), avoiding a hydration mismatch; the stored value is then
 * applied on the first post-hydration commit.
 */
export const useDirectoryFilterOpenState = (
    key: string,
): { open: boolean; toggle: () => void } => {
    const open = useSyncExternalStore(
        (notify) => subscribe(key, notify),
        () => readOpen(key),
        () => false,
    );
    const toggle = useCallback(() => writeOpen(key, !readOpen(key)), [key]);
    return { open, toggle };
};

const DirectoryFilterDrawerContext = createContext<DirectoryFilterDrawer>({
    open: false,
    toggle: () => {},
    hasSidebar: false,
});

export const DirectoryFilterDrawerProvider =
    DirectoryFilterDrawerContext.Provider;

export const useDirectoryFilterDrawer = (): DirectoryFilterDrawer =>
    useContext(DirectoryFilterDrawerContext);
